import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { buildDesktopAuthDeepLink, getDesktopAccountHintFromHost } from 'utils/magic-link-agent';
import { useTranslate } from 'hooks/use-translation';
const Wrapper = styled.div`
  max-width: 420px;
  margin: 48px;
`;
const Title = styled.h1`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
  color: #374151;
`;
const Body = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: #6b7280;
`;
const OpenLink = styled.a`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 6px;
  background: #3b82f6;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    background: #2563eb;
  }
`;
const ContinueButton = styled.button`
  display: block;
  margin-top: 16px;
  padding: 0;
  border: 0;
  background: none;
  color: #2563eb;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #1d4ed8;
  }
`;
const FALLBACK_MS = 2000;
const currentWebPath = ()=>`${window.location.pathname}${window.location.search}${window.location.hash}`;
const stripAgentFromPath = (pathWithSearch)=>{
    const url = new URL(pathWithSearch, 'https://rocketlane.invalid');
    url.searchParams.delete('agent');
    return `${url.pathname}${url.search}${url.hash}`;
};
/**
 * Non-browser magic-link landing: immediately open the desktop custom scheme so
 * the Tauri app redeems the token. If the app does not take over, continue the
 * existing web login/consume path.
 */ export const DesktopMagicLinkBounce = ({ token, agent, onContinueInBrowser })=>{
    const translate = useTranslate();
    const onContinueRef = useRef(onContinueInBrowser);
    onContinueRef.current = onContinueInBrowser;
    const webPathRef = useRef(currentWebPath());
    const deepLink = buildDesktopAuthDeepLink({
        token,
        agent,
        account: getDesktopAccountHintFromHost()
    });
    // Reads refs only, so it stays stable and the effect below is not re-armed
    // (which would re-attempt the deep link) on every render.
    const continueInBrowser = useCallback(()=>{
        if (onContinueRef.current) {
            onContinueRef.current();
            return;
        }
        window.location.replace(stripAgentFromPath(webPathRef.current));
    }, []);
    useEffect(()=>{
        window.location.href = deepLink;
        const timeoutId = window.setTimeout(()=>{
            if (!document.hidden) {
                continueInBrowser();
            }
        }, FALLBACK_MS);
        const onVisibility = ()=>{
            if (document.hidden) {
                window.clearTimeout(timeoutId);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return ()=>{
            window.clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [
        continueInBrowser,
        deepLink
    ]);
    return /*#__PURE__*/ _jsxs(Wrapper, {
        "data-cy": "desktop-magic-link-bounce",
        children: [
            /*#__PURE__*/ _jsx(Title, {
                "data-cy": "desktop-magic-link-bounce-title",
                children: translate('desktop.magic_link.bounce.title')
            }),
            /*#__PURE__*/ _jsx(Body, {
                children: translate('desktop.magic_link.bounce.body')
            }),
            /*#__PURE__*/ _jsx(OpenLink, {
                href: deepLink,
                children: translate('desktop.magic_link.bounce.open')
            }),
            /*#__PURE__*/ _jsx(ContinueButton, {
                type: "button",
                "data-cy": "desktop-magic-link-bounce-continue",
                onClick: continueInBrowser,
                children: translate('desktop.magic_link.bounce.continue')
            })
        ]
    });
};
